import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import type { GroupListItemDataFragment } from '@/gql/graphql';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import type { GroupFormData } from '../shared/GroupFormConfig';
import GroupFormContainer from '../shared/GroupFormContainer';
import { UpdateGroupMutation } from './UpdateGroup.graphql';
import '@/utils/notifications';
import type { UpdateGroupMutationVariables } from './UpdateGroup.graphql';

interface UpdateGroupProps {
    open: boolean;
    group?: GroupListItemDataFragment;
    onClose: () => void;
    onCreated: () => void;
}

const EditGroup = ({ open, onClose, onCreated, group }: UpdateGroupProps) => {
    const { t } = useTranslation('groupDetails');

    const handleSubmit = async (formData: GroupFormData) => {
        try {
            const variables: UpdateGroupMutationVariables = {
                id: group?.id || '',
                input: formData,
            };

            const { data } = await client.mutate({
                mutation: UpdateGroupMutation,
                variables,
            });

            if (data?.updateGroup?.__typename === 'UpdateGroupSuccessfulResponse') {
                if (onCreated) {
                    onCreated();
                } else {
                    onClose();
                }
                showSuccessNotification({
                    message: t('notifications.updateGroupTitle'),
                    description: t('notifications.updateGroupDescription', { name: formData.name }),
                });
                return;
            }

            if (data?.updateGroup?.__typename === 'ErrorResponse') {
                const error = {
                    fields: data.updateGroup.fields,
                };
                showErrorNotification({
                    message: t('notifications.updateErrorTitle'),
                    description: error.fields?.[0]?.message || t('notifications.updateErrorDescription'),
                });
                throw error;
            }

            throw new Error('Unknown error creating group');
        } catch (error) {
            console.error('Error creating group:', error);
            // Handle error appropriately
        }
    };
    return (
        <GroupFormContainer
            mode="edit"
            group={group}
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            onOpenChange={onClose}
            title={t('modalEditGroup.title')}
        />
    );
};

export default EditGroup;
